// WB-M1B S1 (doc 23 §16.3.7, §16.5, §16.12.1) -- the board equation renderer:
// LaTeX source in, one self-contained SVG picture and its natural size out.
//
// Not connected to the board yet. Nothing here knows Excalidraw, the scene
// codec or the store; S2 builds on this surface.
//
// The renderer is local and deterministic: no server, no remote service, no
// DOM insertion, no HTML path. MathJax lives in `boardEquationEngine.js`,
// which is loaded on first need as its own chunk.
//
// Trust model (doc 23 §16.5): the source is the Student's own text and the
// result is only ever shown as an IMAGE. Even so, nothing MathJax produces is
// passed through as markup. The engine hands over a plain tree; this module
// checks every element, attribute and value of it against the policy below
// and writes the SVG string ITSELF. Whatever the policy does not name cannot
// be in the output, and anything it does not expect is a failure, never a
// silent drop.
//
// The policy is the minimum the S1 corpus needs (the element and attribute
// inventory of MathJax 3.2.2, TeX base + ams, SVG output, fontCache none --
// see tests/unit/services/aiTutor/boardEquation.corpus.js). Fixed denials
// hold whatever a corpus shows: no script, no event handler, no link, no
// external or internal reference, no <foreignObject>, no <image>, no style
// or class in the output, no URL of any scheme.
//
// M1B scope (Technical Lead, 2026-10-03): mathematics, school Physics and
// Chemistry notation, Latin / Persian / Arabic-Indic digits. Persian and
// other right-to-left prose is refused before rendering
// (`rtl_text_unsupported`); it belongs in the board's text tool.
//
// Failures are typed (`BoardEquationError.code`) and never carry the source.

// Board ink (BoardEditor's INK). The picture is drawn in it; MathJax's
// `currentColor` would be black inside an image.
export const BOARD_EQUATION_INK = '#0b163f'

// Provisional S1 limits (doc 23 §16.5). The source limit may be lowered by
// evidence, never raised here.
export const BOARD_EQUATION_LIMITS = Object.freeze({
  maxSourceChars: 1000,
  maxBraceDepth: 32,
  // The picture: the S1 corpus peaks far below these (see the S1 report).
  maxSvgBytes: 256 * 1024,
  maxSvgNodes: 6000,
  maxNaturalWidthEm: 400,
  maxNaturalHeightEm: 200,
})

// Natural size of the SVG's own width / height attributes. Only an intrinsic
// size for an <img>; the layout unit of the picture is the em (viewBox units
// are 1/1000 em), and a caller scales it by its own em.
const INTRINSIC_EM_PX = 16

// Clear space around the drawn equation, on every side (S1 recovery).
// MathJax lays out with font METRICS and assumes `overflow: visible`; an
// image clips at its box, and S1 saw outline ink up to 0.025 em outside
// MathJax's box (italic overhangs, root signs, antialiasing), more for \rlap.
// The picture's box is therefore the union of MathJax's box and the ink the
// picture actually draws (computed from its own geometry, below), plus this.
export const BOARD_EQUATION_MARGIN_EM = 0.05
const MARGIN_UNITS = BOARD_EQUATION_MARGIN_EM * 1000

// The font a Persian / Arabic-Indic digit is drawn in. MathJax's fonts have
// no such digits, so the device draws them; MathJax asks for `serif`, which
// on Windows mixed Tahoma and Times New Roman inside one number. A fixed list
// of font NAMES (never a file or a URL): Windows, Android, macOS, then the
// generic family.
export const BOARD_EQUATION_DIGIT_FONT =
  "Tahoma, 'Segoe UI', 'Noto Sans Arabic', 'Noto Naskh Arabic', 'Geeza Pro', sans-serif"
// Where a character drawn by the device's font may put ink, in 1/1000 em:
// the advance MathJax gave it (the engine's `unknownCharWidth`; CJK is one
// em; U+066B / U+066C the engine's `separatorCharWidth`), and MathJax's own
// default height / depth for such a character. The engine lays digits out
// with TeX's smaller 0-9 box; their ink is still allowed the larger one,
// because the font is the device's (Tahoma: 0.622 up, 0.691 bold, nothing
// down -- S1 recovery evidence).
const UNKNOWN_ADVANCE = 500
const UNKNOWN_WIDE_ADVANCE = 1000
const SEPARATOR_ADVANCE = 320
// bold digits and ٪: the engine's `boldDigitCharWidth` / `boldPercentCharWidth`
const BOLD_DIGIT_ADVANCE = 560
const BOLD_PERCENT_ADVANCE = 650
const UNKNOWN_HEIGHT = 750
const UNKNOWN_DEPTH = 200

export const BOARD_EQUATION_ERROR_CODES = Object.freeze([
  'not_a_string',
  'empty',
  'too_long',
  'bad_characters',
  'rtl_text_unsupported',
  'too_deep',
  'unsupported_command',
  'engine_unavailable',
  'syntax',
  'render_failed',
  'unsafe_output',
  'output_too_large',
])

/**
 * A typed renderer failure. `message` is the code -- by construction it can
 * never contain the equation's source.
 */
export class BoardEquationError extends Error {
  /**
   * @param {string} code -- one of BOARD_EQUATION_ERROR_CODES
   * @param {string|null} [detail] -- for `syntax`: MathJax's error category
   *   (e.g. `MissingCloseBrace`); for `unsupported_command`: nothing. Never
   *   source text.
   */
  constructor(code, detail) {
    super(code)
    this.name = 'BoardEquationError'
    this.code = code
    this.detail = detail || null
  }
}

function fail(code, detail) {
  throw new BoardEquationError(code, detail)
}

// ---------------------------------------------------------------------------
// Source validation -- runs before the engine is even loaded.
// ---------------------------------------------------------------------------

// The accepted board contract (Subway's AiTutorBoardScene): C0 controls other
// than \t \n \r, and DEL, are refused; so are U+0000 and unpaired surrogates,
// which PostgreSQL jsonb cannot store.
function hasForbiddenControl(value) {
  for (let i = 0; i < value.length; i++) {
    const unit = value.charCodeAt(i)
    if (unit === 127) return true
    if (unit < 32 && unit !== 9 && unit !== 10 && unit !== 13) return true
  }
  return false
}

function isStorableString(value) {
  for (let i = 0; i < value.length; i++) {
    const unit = value.charCodeAt(i)
    if (unit === 0) return false
    if (unit >= 0xd800 && unit <= 0xdbff) {
      const next = i + 1 < value.length ? value.charCodeAt(i + 1) : 0
      if (next < 0xdc00 || next > 0xdfff) return false
      i++
    } else if (unit >= 0xdc00 && unit <= 0xdfff) {
      return false
    }
  }
  return true
}

// Right-to-left script (M1B product scope, Technical Lead 2026-10-03).
//
// Persian / Arabic-script words are not supported INSIDE an equation in M1B:
// MathJax draws every such letter as its own text node, left to right, so
// words come out unjoined and reversed (S1 STOP evidence). They are refused,
// never transformed or reordered; the Student writes Persian prose with the
// board's text tool next to the formula.
//
// Refused: every strong right-to-left script and the right-to-left
// formatting controls. Allowed, although they sit inside the Arabic block:
// the Persian (U+06F0-06F9) and Arabic-Indic (U+0660-0669) DIGITS, their
// decimal / thousands separators (U+066B / U+066C) and the Persian percent
// sign (U+066A, Technical Lead 2026-10-03) -- numeric notation, not prose;
// the engine reads them as numbers and `\%` (`bornaArabicDigits`).
const RTL_RANGES = Object.freeze([
  // Hebrew, Arabic, Syriac, Arabic Supplement, Thaana, NKo, Samaritan,
  // Mandaic, Syriac Supplement, Arabic Extended-B and -A
  [0x0590, 0x08ff],
  // Hebrew and Arabic presentation forms (A)
  [0xfb1d, 0xfdff],
  // Arabic presentation forms (B); U+FEFF (byte-order mark) is not one
  [0xfe70, 0xfefe],
  // right-to-left scripts of the supplementary plane (Rumi numerals, Yezidi,
  // Arabic Extended-C, ...)
  [0x10800, 0x10fff],
  // Mende Kikakui, Adlam, Indic / Ottoman Siyaq numbers, Arabic
  // mathematical alphabetic symbols
  [0x1e800, 0x1efff],
])
// RIGHT-TO-LEFT MARK, -EMBEDDING, -OVERRIDE, -ISOLATE. (U+061C ARABIC
// LETTER MARK is inside the Arabic block above.)
const RTL_CONTROLS = Object.freeze([0x200f, 0x202b, 0x202e, 0x2067])

function isAllowedArabicScriptNumeral(code) {
  return (
    (code >= 0x0660 && code <= 0x0669) ||
    (code >= 0x06f0 && code <= 0x06f9) ||
    code === 0x066a ||
    code === 0x066b ||
    code === 0x066c
  )
}

function isRtlCodePoint(code) {
  if (RTL_CONTROLS.indexOf(code) !== -1) return true
  if (isAllowedArabicScriptNumeral(code)) return false
  for (let i = 0; i < RTL_RANGES.length; i++) {
    if (code >= RTL_RANGES[i][0] && code <= RTL_RANGES[i][1]) return true
  }
  return false
}

// Runs after the character contract, so surrogates here are always paired.
function hasRtlText(value) {
  for (let i = 0; i < value.length; i++) {
    const code = value.codePointAt(i)
    if (isRtlCodePoint(code)) return true
    if (code > 0xffff) i++
  }
  return false
}

// Control words that are refused by name.
//
// The first group EXISTS in the loaded configuration (base + ams) and was
// seen, in S1, to do what the fixed principles forbid:
//   mmlToken          sets href / style / class / id / font-family on a token;
//   ref, eqref        emit <a href="#..."> with a pointer-events hit box;
//   label, tag,       create ids / equation labels -- state that outlives the
//   notag, nonumber   equation and feeds \ref;
//   DeclareMathOperator  defines a macro that outlives the equation.
// The second group is NOT loaded (MathJax would answer "undefined control
// sequence"); it is named so that the refusal is typed and happens before the
// engine is loaded.
const DENIED_COMMANDS = Object.freeze([
  'mmlToken',
  'ref',
  'eqref',
  'label',
  'tag',
  'notag',
  'nonumber',
  'DeclareMathOperator',
  // macro definition
  'def',
  'edef',
  'gdef',
  'xdef',
  'let',
  'newcommand',
  'renewcommand',
  'providecommand',
  'newenvironment',
  'renewenvironment',
  'csname',
  'endcsname',
  'catcode',
  // package loading
  'require',
  // links, attributes, styling, external content
  'href',
  'url',
  'class',
  'cssId',
  'style',
  'data',
  'unicode',
  'includegraphics',
  'input',
  'include',
  'bbox',
  'color',
  'textcolor',
  'colorbox',
  'fcolorbox',
  'definecolor',
  'enclose',
  'toggle',
  'endtoggle',
  'mathtip',
  'texttip',
  'verb',
])

const DENIED = DENIED_COMMANDS.reduce((set, name) => {
  set[name] = true
  return set
}, Object.create(null))

function isLetter(code) {
  return (code >= 65 && code <= 90) || (code >= 97 && code <= 122)
}

// One pass over the source as TeX reads it: a backslash followed by letters
// is a control word, a backslash followed by anything else is a control
// symbol (so `\\ref` is a line break and the letters "ref", not `\ref`).
function scanSource(source) {
  let depth = 0
  for (let i = 0; i < source.length; i++) {
    const code = source.charCodeAt(i)
    if (code === 92 /* \ */) {
      let j = i + 1
      while (j < source.length && isLetter(source.charCodeAt(j))) j++
      if (j === i + 1) {
        // control symbol (or a trailing backslash): skip the one character
        i += 1
        continue
      }
      if (DENIED[source.slice(i + 1, j)]) fail('unsupported_command')
      i = j - 1
    } else if (code === 123 /* { */) {
      depth += 1
      if (depth > BOARD_EQUATION_LIMITS.maxBraceDepth) fail('too_deep')
    } else if (code === 125 /* } */) {
      if (depth > 0) depth -= 1
    }
  }
}

/**
 * Checks a source against the S1 rules. Throws a typed error; returns
 * nothing. Never loads the engine.
 * @param {*} source
 * @throws {BoardEquationError}
 */
export function validateEquationSource(source) {
  if (typeof source !== 'string') fail('not_a_string')
  if (source.length > BOARD_EQUATION_LIMITS.maxSourceChars) fail('too_long')
  if (source.trim() === '') fail('empty')
  if (hasForbiddenControl(source) || !isStorableString(source)) {
    fail('bad_characters')
  }
  if (hasRtlText(source)) fail('rtl_text_unsupported')
  scanSource(source)
}

// ---------------------------------------------------------------------------
// The picture: policy check and canonical serialization of the engine's tree.
// ---------------------------------------------------------------------------

const SVG_NS = 'http://www.w3.org/2000/svg'

const NUMBER = '-?(?:\\d+\\.?\\d*|\\.\\d+)'
const NUMBER_RE = new RegExp(`^${NUMBER}$`)
const VIEWBOX_RE = new RegExp(`^${NUMBER} ${NUMBER} ${NUMBER} ${NUMBER}$`)
// translate(x) / translate(x,y) / translate(x y) / scale(s) / scale(sx,sy),
// one or more, space separated. Nothing else: no matrix, rotate, skew.
const TRANSFORM_RE = new RegExp(
  `^(?:(?:translate|scale)\\(${NUMBER}(?:(?:, ?| )${NUMBER})?\\))(?: (?:translate|scale)\\(${NUMBER}(?:(?:, ?| )${NUMBER})?\\))*$`
)
// SVG path data exactly as MathJax 3.2.2's TeX SVG font writes it: all 2,313
// glyph outlines of output/svg/fonts/tex use only M L H V Q T Z, digits,
// spaces and minus signs (S1 enumeration). Nothing else is accepted.
const PATH_RE = /^[MLHVQTZ0-9 -]+$/
const EX_RE = /^\d+(?:\.\d+)?ex$/
const ROOT_STYLE_RE = /^vertical-align: -?\d+(?:\.\d+)?(?:ex)?;$/
const FONT_SIZE_RE = /^\d+(?:\.\d+)?px$/
// Values of the data-* hints MathJax leaves on its nodes. They are read (to
// know what a node is) and never written to the output.
const HINT_RE = /^[A-Za-z0-9 _-]{1,40}$/
// Characters XML 1.0 cannot carry at all: the forbidden controls and the two
// noncharacters U+FFFE / U+FFFF.
function hasXmlInvalid(value) {
  if (hasForbiddenControl(value)) return true
  for (let i = 0; i < value.length; i++) {
    const unit = value.charCodeAt(i)
    if (unit === 0xfffe || unit === 0xffff) return true
  }
  return false
}

const MAX_ATTR_CHARS = 20000
const MAX_TEXT_CHARS = 64
const LINE_CLASSES = ['mjx-solid', 'mjx-dashed', 'mjx-dotted']

function unsafe() {
  return fail('unsafe_output')
}

function own(object, key) {
  return Object.prototype.hasOwnProperty.call(object, key)
}

// Every attribute of `node` must be named in `allowed`.
function onlyAttrs(node, allowed) {
  Object.keys(node.attrs).forEach(name => {
    if (allowed.indexOf(name) === -1) unsafe()
    const value = node.attrs[name]
    if (typeof value !== 'string' || value.length > MAX_ATTR_CHARS) unsafe()
  })
}

function number(node, name, required) {
  if (!own(node.attrs, name)) {
    if (required) unsafe()
    return null
  }
  const value = node.attrs[name]
  if (value.length > 24 || !NUMBER_RE.test(value)) unsafe()
  return value
}

function transformOf(node) {
  if (!own(node.attrs, 'transform')) return ''
  const value = node.attrs.transform
  if (value.length > 200 || !TRANSFORM_RE.test(value)) unsafe()
  return ` transform="${value}"`
}

// ---- Where the picture draws ink -----------------------------------------
//
// Every transform the policy lets through is translate / scale, so a place
// in the picture is (sx, sy, tx, ty): x' = sx x + tx, y' = sy y + ty. The
// bounds are kept in viewBox units and are conservative: a curve is bounded
// by its control points, a stroke by half its width.

// The root group's scale(1,-1): MathJax's y-up layout into the viewBox.
const ROOT_PLACE = Object.freeze({ sx: 1, sy: -1, tx: 0, ty: 0 })

function compose(outer, inner) {
  return {
    sx: outer.sx * inner.sx,
    sy: outer.sy * inner.sy,
    tx: outer.sx * inner.tx + outer.tx,
    ty: outer.sy * inner.ty + outer.ty,
  }
}

// `node`'s own transform applied inside `place`. Already checked by
// transformOf / TRANSFORM_RE.
function placeOf(node, place) {
  if (!own(node.attrs, 'transform')) return place
  let out = place
  const step = /(translate|scale)\(([^)]*)\)/g
  let m
  while ((m = step.exec(node.attrs.transform))) {
    const n = m[2].split(/, ?| /).map(Number)
    if (m[1] === 'translate') {
      out = compose(out, {
        sx: 1,
        sy: 1,
        tx: n[0],
        ty: n.length > 1 ? n[1] : 0,
      })
    } else {
      out = compose(out, {
        sx: n[0],
        sy: n.length > 1 ? n[1] : n[0],
        tx: 0,
        ty: 0,
      })
    }
  }
  return out
}

function inkRect(state, place, x0, y0, x1, y1) {
  const ax = place.sx * x0 + place.tx
  const bx = place.sx * x1 + place.tx
  const ay = place.sy * y0 + place.ty
  const by = place.sy * y1 + place.ty
  const ink = state.ink
  ink.minX = Math.min(ink.minX, ax, bx)
  ink.maxX = Math.max(ink.maxX, ax, bx)
  ink.minY = Math.min(ink.minY, ay, by)
  ink.maxY = Math.max(ink.maxY, ay, by)
}

// The control-point box of path data (absolute M L H V Q T Z only, integer
// coordinates: PATH_RE). Malformed data is unsafe output.
function pathBox(d) {
  const tokens = d.match(/[MLHVQTZ]|-?\d+/g) || []
  const box = {
    minX: Infinity,
    minY: Infinity,
    maxX: -Infinity,
    maxY: -Infinity,
  }
  const add = (x, y) => {
    if (x < box.minX) box.minX = x
    if (x > box.maxX) box.maxX = x
    if (y < box.minY) box.minY = y
    if (y > box.maxY) box.maxY = y
  }
  let cx = 0
  let cy = 0
  let startX = 0
  let startY = 0
  let ctrl = null
  let i = 0
  while (i < tokens.length) {
    const command = tokens[i++]
    if (!/^[MLHVQTZ]$/.test(command)) unsafe()
    const args = []
    while (i < tokens.length && !/^[MLHVQTZ]$/.test(tokens[i])) {
      args.push(Number(tokens[i++]))
    }
    const per = { M: 2, L: 2, H: 1, V: 1, Q: 4, T: 2, Z: 0 }[command]
    if (
      per === 0 ? args.length !== 0 : args.length === 0 || args.length % per
    ) {
      unsafe()
    }
    if (command === 'Z') {
      cx = startX
      cy = startY
      ctrl = null
      continue
    }
    for (let k = 0; k < args.length; k += per) {
      if (command === 'H') cx = args[k]
      else if (command === 'V') cy = args[k]
      else if (command === 'Q') {
        add(args[k], args[k + 1])
        ctrl = [args[k], args[k + 1]]
        cx = args[k + 2]
        cy = args[k + 3]
      } else if (command === 'T') {
        ctrl = ctrl ? [2 * cx - ctrl[0], 2 * cy - ctrl[1]] : [cx, cy]
        add(ctrl[0], ctrl[1])
        cx = args[k]
        cy = args[k + 1]
      } else {
        cx = args[k]
        cy = args[k + 1]
        if (command === 'M' && k === 0) {
          startX = cx
          startY = cy
        }
      }
      if (command !== 'Q' && command !== 'T') ctrl = null
      add(cx, cy)
    }
  }
  return box.minX === Infinity ? null : box
}

function isWide(code) {
  return code >= 0x2e80
}

function isArabicScriptNumeralText(text) {
  for (let i = 0; i < text.length; i++) {
    if (!isAllowedArabicScriptNumeral(text.charCodeAt(i))) return false
  }
  return true
}

function hint(node, name, allowedValues) {
  if (!own(node.attrs, name)) return null
  const value = node.attrs[name]
  if (!HINT_RE.test(value)) unsafe()
  if (allowedValues && allowedValues.indexOf(value) === -1) unsafe()
  return value
}

function escapeText(value) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

// MathJax expects a stylesheet next to its SVG (svg.js / mtable.js
// `styles`). An image has none, so the three rules that change what is
// drawn are written as presentation attributes instead:
//   path[data-c]                      stroke-width: 3
//   line[data-line], rect[data-frame] stroke-width: 70; fill: none
//   .mjx-dashed / .mjx-dotted         stroke-dasharray (and round caps)
const TABLE_RULE = 70

function lineStyle(node) {
  const cls = own(node.attrs, 'class') ? node.attrs.class : 'mjx-solid'
  if (LINE_CLASSES.indexOf(cls) === -1) unsafe()
  const width = ` stroke-width="${TABLE_RULE}"`
  if (cls === 'mjx-dashed') return `${width} stroke-dasharray="140"`
  if (cls === 'mjx-dotted') {
    return `${width} stroke-linecap="round" stroke-dasharray="0,140"`
  }
  return width
}

const G_ATTRS = [
  'transform',
  'data-mml-node',
  'data-mjx-texclass',
  'data-c',
  'data-width-includes-label',
]

function emitGroup(node, state, place) {
  onlyAttrs(node, G_ATTRS)
  hint(node, 'data-mml-node')
  hint(node, 'data-mjx-texclass')
  hint(node, 'data-c')
  hint(node, 'data-width-includes-label')
  const transform = transformOf(node)
  return `<g${transform}>${emitChildren(
    node,
    state,
    false,
    placeOf(node, place)
  )}</g>`
}

const GLYPH_STROKE = 3

function emitPath(node, state, place) {
  onlyAttrs(node, ['d', 'data-c', 'transform'])
  if (node.children.length !== 0) unsafe()
  if (!own(node.attrs, 'd')) unsafe()
  const glyph = hint(node, 'data-c') !== null
  // A glyph with no outline (a space): nothing to draw, nothing to write.
  if (node.attrs.d === '') return ''
  if (!PATH_RE.test(node.attrs.d)) unsafe()
  const transform = transformOf(node)
  if (place) {
    const box = pathBox(node.attrs.d)
    const half = glyph ? GLYPH_STROKE / 2 : 0
    if (box) {
      inkRect(
        state,
        placeOf(node, place),
        box.minX - half,
        box.minY - half,
        box.maxX + half,
        box.maxY + half
      )
    }
  }
  return `<path${
    glyph ? ` stroke-width="${GLYPH_STROKE}"` : ''
  }${transform} d="${node.attrs.d}"/>`
}

function emitRect(node, state, place) {
  onlyAttrs(node, [
    'x',
    'y',
    'width',
    'height',
    'fill',
    'stroke-width',
    'class',
    'data-frame',
    'data-bgcolor',
  ])
  if (node.children.length !== 0) unsafe()
  const x = number(node, 'x', false)
  const y = number(node, 'y', false)
  const width = number(node, 'width', true)
  const height = number(node, 'height', true)
  let out = '<rect'
  if (x !== null) out += ` x="${x}"`
  if (y !== null) out += ` y="${y}"`
  out += ` width="${width}" height="${height}"`
  // The rectangle, grown by half the stroke drawn around it.
  const ink = half => {
    const x0 = x === null ? 0 : Number(x)
    const y0 = y === null ? 0 : Number(y)
    inkRect(
      state,
      place,
      x0 - half,
      y0 - half,
      x0 + Number(width) + half,
      y0 + Number(height) + half
    )
  }
  hint(node, 'data-bgcolor', ['true'])
  if (hint(node, 'data-frame', ['true']) !== null) {
    // A table frame: an outline, never filled.
    ink(TABLE_RULE / 2)
    return `${out} fill="none"${lineStyle(node)}/>`
  }
  if (own(node.attrs, 'class')) unsafe()
  if (own(node.attrs, 'fill')) {
    const fill = node.attrs.fill
    // `black` is \rule; it is drawn in the board's ink like everything else.
    if (fill === 'none') out += ' fill="none"'
    else if (fill !== 'black') unsafe()
  }
  const strokeWidth = number(node, 'stroke-width', false)
  if (strokeWidth !== null) out += ` stroke-width="${strokeWidth}"`
  ink(strokeWidth === null ? 0 : Math.abs(Number(strokeWidth)) / 2)
  return `${out}/>`
}

// Two kinds of line exist: a table rule (`data-line`, styled by MathJax's
// stylesheet -- see lineStyle) and an enclosure border, which carries its own
// stroke width and nothing else.
function emitLine(node, state, place) {
  onlyAttrs(node, [
    'x1',
    'y1',
    'x2',
    'y2',
    'class',
    'data-line',
    'stroke-width',
  ])
  if (node.children.length !== 0) unsafe()
  const x1 = number(node, 'x1', true)
  const y1 = number(node, 'y1', true)
  const x2 = number(node, 'x2', true)
  const y2 = number(node, 'y2', true)
  const out = `<line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}"`
  // Both ends, grown by half the stroke in every direction (covers caps).
  const ink = half =>
    inkRect(
      state,
      place,
      Math.min(Number(x1), Number(x2)) - half,
      Math.min(Number(y1), Number(y2)) - half,
      Math.max(Number(x1), Number(x2)) + half,
      Math.max(Number(y1), Number(y2)) + half
    )
  if (hint(node, 'data-line', ['h', 'v']) !== null) {
    if (own(node.attrs, 'stroke-width')) unsafe()
    ink(TABLE_RULE / 2)
    return `${out}${lineStyle(node)}/>`
  }
  if (own(node.attrs, 'class')) unsafe()
  const strokeWidth = number(node, 'stroke-width', true)
  ink(Math.abs(Number(strokeWidth)) / 2)
  return `${out} stroke-width="${strokeWidth}"/>`
}

// The generic families MathJax names for a character it has no glyph for,
// by math variant (normal, script, sans-serif, monospace).
const GENERIC_FAMILIES = ['serif', 'cursive', 'sans-serif', 'monospace']

// A character MathJax's fonts do not have. In the M1B scope that is a
// Persian / Arabic-Indic digit (Persian and Arabic letters are refused by the
// validator, and again here). It is drawn by the device's font: digits in
// BOARD_EQUATION_DIGIT_FONT, anything else in `serif` -- font NAMES, never a
// file or a URL.
function emitText(node, state, place) {
  onlyAttrs(node, [
    'transform',
    'font-size',
    'font-family',
    'font-style',
    'font-weight',
    'data-variant',
  ])
  hint(node, 'data-variant')
  if (node.children.length !== 1 || typeof node.children[0].text !== 'string') {
    unsafe()
  }
  const text = node.children[0].text
  if (
    text.length === 0 ||
    text.length > MAX_TEXT_CHARS ||
    hasXmlInvalid(text) ||
    !isStorableString(text) ||
    hasRtlText(text)
  ) {
    unsafe()
  }
  const digits = isArabicScriptNumeralText(text)
  const family = node.attrs['font-family']
  if (digits ? GENERIC_FAMILIES.indexOf(family) === -1 : family !== 'serif') {
    unsafe()
  }
  if (!FONT_SIZE_RE.test(node.attrs['font-size'] || '')) unsafe()
  let out = `<text${transformOf(node)} font-family="${
    digits ? BOARD_EQUATION_DIGIT_FONT : 'serif'
  }" font-size="${node.attrs['font-size']}"`
  if (own(node.attrs, 'font-style')) {
    if (node.attrs['font-style'] !== 'italic') unsafe()
    out += ' font-style="italic"'
  }
  if (own(node.attrs, 'font-weight')) {
    if (node.attrs['font-weight'] !== 'bold') unsafe()
    out += ' font-weight="bold"'
  }
  // The box MathJax reserved for the text (it cannot measure the device's
  // font): one advance per character, its assumed height and depth. The text
  // node is flipped (scale(1,-1)), so above the baseline is negative y here.
  const bold = node.attrs['font-weight'] === 'bold'
  let advance = 0
  for (let i = 0; i < text.length; i++) {
    const code = text.codePointAt(i)
    if (code === 0x066b || code === 0x066c) advance += SEPARATOR_ADVANCE
    else if (bold && code === 0x066a) advance += BOLD_PERCENT_ADVANCE
    else if (bold && isAllowedArabicScriptNumeral(code)) {
      advance += BOLD_DIGIT_ADVANCE
    } else advance += isWide(code) ? UNKNOWN_WIDE_ADVANCE : UNKNOWN_ADVANCE
    if (code > 0xffff) i++
  }
  inkRect(
    state,
    placeOf(node, place),
    0,
    -UNKNOWN_HEIGHT,
    advance,
    UNKNOWN_DEPTH
  )
  return `${out}>${escapeText(text)}</text>`
}

// A nested <svg> is how MathJax clips the pieces of a stretched delimiter or
// arrow. It carries a box and a viewBox, and only glyph outlines inside --
// which it clips, so its ink is at most its own box.
function emitNestedSvg(node, state, place) {
  onlyAttrs(node, ['x', 'y', 'width', 'height', 'viewBox'])
  const viewBox = node.attrs.viewBox || ''
  if (viewBox.length > 100 || !VIEWBOX_RE.test(viewBox)) unsafe()
  const x = number(node, 'x', false)
  const y = number(node, 'y', false)
  const width = number(node, 'width', true)
  const height = number(node, 'height', true)
  let out = '<svg'
  if (x !== null) out += ` x="${x}"`
  if (y !== null) out += ` y="${y}"`
  out += ` width="${width}" height="${height}" viewBox="${viewBox}">`
  const x0 = x === null ? 0 : Number(x)
  const y0 = y === null ? 0 : Number(y)
  inkRect(state, place, x0, y0, x0 + Number(width), y0 + Number(height))
  return `${out}${emitChildren(node, state, true, null)}</svg>`
}

function emitChildren(node, state, insideNestedSvg, place) {
  let out = ''
  for (let i = 0; i < node.children.length; i++) {
    const child = node.children[i]
    // No stray text between elements; text exists only inside <text>.
    if (!child || typeof child.kind !== 'string') unsafe()
    if (!child.attrs || !Array.isArray(child.children)) unsafe()
    state.nodes += 1
    if (state.nodes > BOARD_EQUATION_LIMITS.maxSvgNodes) {
      fail('output_too_large')
    }
    if (child.kind === 'path') out += emitPath(child, state, place)
    else if (insideNestedSvg) unsafe()
    else if (child.kind === 'g') out += emitGroup(child, state, place)
    else if (child.kind === 'rect') out += emitRect(child, state, place)
    else if (child.kind === 'line') out += emitLine(child, state, place)
    else if (child.kind === 'text') out += emitText(child, state, place)
    else if (child.kind === 'svg') out += emitNestedSvg(child, state, place)
    else unsafe()
  }
  return out
}

function round3(value) {
  return Math.round(value * 1000) / 1000
}

/**
 * Checks the engine's tree against the policy and writes the picture.
 * Pure: no engine, no DOM. Exported for the specs.
 * @param {{kind: string, attrs: object, children: Array}} tree -- MathJax's
 *   root <svg> as a plain tree
 * @returns {{svg: string, metrics: {widthEm: number, heightEm: number,
 *   ascentEm: number, depthEm: number}}}
 * @throws {BoardEquationError} `unsafe_output` / `output_too_large`
 */
export function canonicalizeEquationSvg(tree) {
  if (
    !tree ||
    tree.kind !== 'svg' ||
    !tree.attrs ||
    !Array.isArray(tree.children)
  ) {
    unsafe()
  }
  onlyAttrs(tree, [
    'xmlns',
    'width',
    'height',
    'role',
    'focusable',
    'viewBox',
    'style',
  ])
  if (tree.attrs.xmlns !== SVG_NS) unsafe()
  if (tree.attrs.role !== 'img' || tree.attrs.focusable !== 'false') unsafe()
  // MathJax's own inline size and baseline shift; read, never written.
  if (!EX_RE.test(tree.attrs.width || '')) unsafe()
  if (!EX_RE.test(tree.attrs.height || '')) unsafe()
  if (!ROOT_STYLE_RE.test(tree.attrs.style || '')) unsafe()
  const viewBox = tree.attrs.viewBox || ''
  if (viewBox.length > 100 || !VIEWBOX_RE.test(viewBox)) unsafe()
  const box = viewBox.split(' ').map(Number)
  if (box.some(n => !Number.isFinite(n)) || box[2] <= 0 || box[3] <= 0) {
    unsafe()
  }

  if (tree.children.length !== 1) unsafe()
  const root = tree.children[0]
  if (!root || root.kind !== 'g' || !root.attrs) unsafe()
  onlyAttrs(root, ['stroke', 'fill', 'stroke-width', 'transform'])
  if (
    root.attrs.stroke !== 'currentColor' ||
    root.attrs.fill !== 'currentColor' ||
    root.attrs['stroke-width'] !== '0' ||
    root.attrs.transform !== 'scale(1,-1)'
  ) {
    unsafe()
  }

  const tooLarge = (width, height) =>
    width / 1000 > BOARD_EQUATION_LIMITS.maxNaturalWidthEm ||
    height / 1000 > BOARD_EQUATION_LIMITS.maxNaturalHeightEm
  // MathJax's own box first: a hopeless picture is refused before any work.
  if (tooLarge(box[2], box[3])) fail('output_too_large')

  // MathJax's box, grown to the ink actually drawn (in viewBox units, where
  // the root group's scale(1,-1) puts the baseline at y = 0), then the margin
  // on every side, rounded outward to whole units.
  const state = {
    nodes: 1,
    ink: {
      minX: box[0],
      minY: box[1],
      maxX: box[0] + box[2],
      maxY: box[1] + box[3],
    },
  }
  const body = emitChildren(root, state, false, ROOT_PLACE)
  const left = Math.floor(state.ink.minX - MARGIN_UNITS)
  const top = Math.floor(state.ink.minY - MARGIN_UNITS)
  const width = Math.ceil(state.ink.maxX + MARGIN_UNITS) - left
  const height = Math.ceil(state.ink.maxY + MARGIN_UNITS) - top
  if (![left, top, width, height].every(Number.isFinite)) unsafe()
  if (tooLarge(width, height)) fail('output_too_large')

  // The picture's natural size in em, margin included: what a caller scales
  // by its own em. viewBox units are 1/1000 em; the baseline is y = 0.
  const metrics = {
    widthEm: width / 1000,
    heightEm: height / 1000,
    ascentEm: -top / 1000,
    depthEm: (height + top) / 1000,
  }
  const svg =
    `<svg xmlns="${SVG_NS}" viewBox="${left} ${top} ${width} ${height}" width="${round3(
      metrics.widthEm * INTRINSIC_EM_PX
    )}" height="${round3(metrics.heightEm * INTRINSIC_EM_PX)}">` +
    `<g fill="${BOARD_EQUATION_INK}" stroke="${BOARD_EQUATION_INK}" stroke-width="0" transform="scale(1,-1)">` +
    `${body}</g></svg>`
  if (utf8Bytes(svg) > BOARD_EQUATION_LIMITS.maxSvgBytes) {
    fail('output_too_large')
  }
  return { svg, metrics }
}

function utf8Bytes(text) {
  let bytes = 0
  for (let i = 0; i < text.length; i++) {
    const unit = text.charCodeAt(i)
    if (unit < 0x80) bytes += 1
    else if (unit < 0x800) bytes += 2
    else if (unit >= 0xd800 && unit <= 0xdbff) {
      bytes += 4
      i++
    } else bytes += 3
  }
  return bytes
}

// ---------------------------------------------------------------------------
// Content identity.
// ---------------------------------------------------------------------------

// cyrb53: a 53-bit string hash. Two differently seeded runs give 106 bits --
// an identity for "the same picture", not a security boundary: nothing
// derived from it is persisted or trusted (doc 23 §16.3.6).
function cyrb53(text, seed) {
  let h1 = 0xdeadbeef ^ seed
  let h2 = 0x41c6ce57 ^ seed
  for (let i = 0; i < text.length; i++) {
    const unit = text.charCodeAt(i)
    h1 = Math.imul(h1 ^ unit, 2654435761)
    h2 = Math.imul(h2 ^ unit, 1597334677)
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507)
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909)
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507)
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909)
  return 4294967296 * (2097151 & h2) + (h1 >>> 0)
}

function hex14(value) {
  return ('00000000000000' + value.toString(16)).slice(-14)
}

/**
 * The deterministic identity of one rendered picture: the same SVG bytes
 * give the same id. This is the content-hash input S2 derives a file id from.
 * @param {string} svg
 * @returns {string} `eq1-` + 28 hex characters
 */
export function equationContentId(svg) {
  return `eq1-${hex14(cyrb53(svg, 0x51))}${hex14(cyrb53(svg, 0x9e3779))}`
}

// ---------------------------------------------------------------------------
// Engine loading and rendering.
// ---------------------------------------------------------------------------

// Doubled in the specs the way the store modules' collaborators are.
export const collaborators = {
  importEngine: () =>
    import(
      /* webpackChunkName: "ai-tutor-board-math" */ './boardEquationEngine'
    ),
}

let enginePromise = null
let engine = null

export function isEquationEngineLoaded() {
  return engine !== null
}

/**
 * Loads MathJax (once). A failed load is not remembered, so a later call
 * tries again.
 * @returns {Promise<void>}
 * @throws {BoardEquationError} `engine_unavailable`
 */
export function loadEquationEngine() {
  if (!enginePromise) {
    enginePromise = Promise.resolve()
      .then(() => collaborators.importEngine())
      .then(
        loaded => {
          if (!loaded || typeof loaded.typesetToTree !== 'function') {
            throw new Error('no engine')
          }
          engine = loaded
        },
        () => {
          throw new Error('no engine')
        }
      )
      .catch(() => {
        enginePromise = null
        engine = null
        return fail('engine_unavailable')
      })
  }
  return enginePromise
}

function renderWith(loaded, source) {
  validateEquationSource(source)
  let tree
  try {
    tree = loaded.typesetToTree(source)
  } catch (err) {
    if (err && err.engineFailure && err.kind === 'syntax') {
      return fail('syntax', err.category)
    }
    return fail('render_failed')
  }
  const { svg, metrics } = canonicalizeEquationSvg(tree)
  return {
    svg,
    metrics,
    contentId: equationContentId(svg),
    svgBytes: utf8Bytes(svg),
  }
}

/**
 * Renders one equation. Loads the engine on first use.
 * @param {string} source -- LaTeX
 * @returns {Promise<{svg: string, contentId: string, svgBytes: number,
 *   metrics: {widthEm: number, heightEm: number, ascentEm: number,
 *   depthEm: number}}>} the picture, its identity and its natural size in em
 * @throws {BoardEquationError}
 */
export async function renderEquation(source) {
  // Refused sources never cause the engine to be downloaded.
  validateEquationSource(source)
  await loadEquationEngine()
  return renderWith(engine, source)
}

/**
 * Renders with an engine that is already loaded (no await): what a caller
 * that must stay synchronous uses after `loadEquationEngine()`.
 * @param {string} source
 * @returns {{svg: string, contentId: string, svgBytes: number, metrics: object}}
 * @throws {BoardEquationError} `engine_unavailable` when it is not loaded
 */
export function renderEquationNow(source) {
  if (!engine) return fail('engine_unavailable')
  return renderWith(engine, source)
}

// Spec-only: forget the loaded engine.
export function resetEquationEngineForTests() {
  enginePromise = null
  engine = null
}
